import { Search, Check, ChevronDown, X } from "lucide-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { blueprintsApi, type Blueprint } from "@/services/blueprints-service";
import { useTranslation } from "react-i18next";
import { useProjects } from "@/contexts/ProjectContext";

interface BlueprintSelectProps {
  value?: string;
  onChange?: (blueprintId: string | undefined) => void;
}

const BlueprintSelect = ({ value, onChange }: BlueprintSelectProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedBlueprint, setSelectedBlueprint] = useState<Blueprint | null>(
    null,
  );
  const pageSize = 10;
  const { selectedProjects } = useProjects();
  const projectScope = useMemo<string[]>(() => {
    const selection: string[] = Array.isArray(selectedProjects)
      ? selectedProjects
      : [];
    return selection.includes("all") ? [] : selection;
  }, [selectedProjects]);
  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery({
      queryKey: [
        "blueprint-select",
        search.trim() || null,
        pageSize,
        projectScope,
      ],
      initialPageParam: 1,
      queryFn: ({ pageParam }) =>
        blueprintsApi.listBlueprints({
          search: search.trim() || null,
          page: pageParam,
          pageSize,
          projectIds: projectScope,
        }),
      getNextPageParam: (lastPage, _allPages, lastPageParam) => {
        const totalPages = lastPage.metadata?.total_pages;
        return totalPages && lastPageParam < totalPages
          ? lastPageParam + 1
          : undefined;
      },
    });

  const blueprints = useMemo(
    () =>
      (data?.pages.flatMap((page) => page.items) ?? []).filter(
        (blueprint) => blueprint.safety_status === "active",
      ),
    [data],
  );

  useEffect(() => {
    if (!value) {
      setSelectedBlueprint(null);
      return;
    }

    const loadedBlueprint = blueprints.find(
      (blueprint) => blueprint.id === value,
    );
    if (loadedBlueprint) setSelectedBlueprint(loadedBlueprint);
  }, [blueprints, value]);

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setSearch("");
      }}
    >
      <div className="relative w-full md:w-48">
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="h-8 w-full justify-between gap-2 rounded-[8px] border-border p-3 pe-6 text-xs font-normal text-foreground hover:bg-card"
            aria-label="Select blueprint (optional)"
          >
            <p className="min-w-0 truncate text-muted-foreground">
              {selectedBlueprint?.name || t("blueprints.noBlueprint")}
            </p>
            {!selectedBlueprint && (
              <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
            )}
          </Button>
        </DropdownMenuTrigger>
        {selectedBlueprint && (
          <button
            type="button"
            aria-label="Clear blueprint selection"
            className="absolute end-3 top-1/2 z-10 flex size-4 -translate-y-1/2 items-center justify-center rounded-[3px] text-muted-foreground"
            onClick={() => {
              setSelectedBlueprint(null);
              onChange?.(undefined);
            }}
          >
            <X className="size-3" />
          </button>
        )}
      </div>
      <DropdownMenuContent
        align="start"
        className="z-[1600] max-h-[320px] w-[min(calc(100vw-2rem),300px)] overflow-y-auto border-border bg-card p-1"
        onCloseAutoFocus={(event) => event.preventDefault()}
        onScroll={(event) => {
          const element = event.currentTarget;
          const reachedBottom =
            element.scrollTop + element.clientHeight >=
            element.scrollHeight - 12;
          if (reachedBottom && hasNextPage && !isFetchingNextPage) {
            void fetchNextPage();
          }
        }}
      >
        <div
          className="relative mb-1"
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Search className="pointer-events-none absolute start-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("blueprints.searchBlueprints")}
            aria-label={t("blueprints.searchBlueprints")}
            className="h-8 w-full rounded-md border border-border bg-transparent ps-7 pe-2 text-xs text-foreground outline-none placeholder:text-muted-foreground focus:border-primary [&::-webkit-search-cancel-button]:opacity-50 [&::-webkit-search-cancel-button]:grayscale"
          />
        </div>
        {isLoading ? (
          <p className="px-2 py-4 text-center text-xs text-muted-foreground">
            {t("blueprints.loadingBlueprints")}
          </p>
        ) : blueprints.length ? (
          <>
            {blueprints.map((blueprint) => (
              <DropdownMenuItem
                key={blueprint.id}
                onSelect={() => {
                  setSelectedBlueprint(blueprint);
                  onChange?.(blueprint.id);
                }}
                className="flex min-w-0 items-center justify-between gap-2 text-xs"
              >
                <span className="min-w-0 truncate">{blueprint.name}</span>
                {blueprint.id === value && (
                  <Check className="size-3.5 shrink-0" />
                )}
              </DropdownMenuItem>
            ))}
            {isFetchingNextPage && (
              <p className="px-2 py-2 text-center text-xs text-muted-foreground">
                {t("blueprints.loadingMoreBlueprints")}
              </p>
            )}
          </>
        ) : (
          <p className="px-2 py-4 text-center text-xs text-muted-foreground">
            {t("blueprints.noBlueprints")}
          </p>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default BlueprintSelect;
